"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Tag } from "@repo/ui/tag";
import { Icon } from "@repo/ui/icon";
import { cn } from "@repo/ui/utils";
import type { CategoryOption, FilterGroup } from "./directory-listing.types";
import { useFilterChips } from "./use-filter-chips";
const MAX_VISIBLE_CHIPS = 5;
interface DirectoryToolbarProps {
  categoryOptions: CategoryOption[];
  filterGroups: FilterGroup[];
  selectedCategories: Set<string>;
  selectedFilters: Record<string, Set<string>>;
  searchQuery: string;
  searchLabel: string;
  onFilterClick: () => void;
  onCategoryRemove: (categoryId: string) => void;
  onFilterRemove: (groupKey: string, optionId: string) => void;
  onClearFilters: () => void;
  onSearchChange: (query: string) => void;
  dateFrom?: string | null;
  dateTo?: string | null;
  hasDateFilter?: boolean;
  onDateRangeChange?: (from: string | null, to: string | null) => void;
}
export function DirectoryToolbar(t0) {
  const $ = _c(61);
  const {
    categoryOptions,
    filterGroups,
    selectedCategories,
    selectedFilters,
    searchQuery,
    searchLabel,
    onFilterClick,
    onCategoryRemove,
    onFilterRemove,
    onClearFilters,
    onSearchChange,
    dateFrom,
    dateTo,
    hasDateFilter,
    onDateRangeChange
  } = t0;
  const t = useTranslations("directory");
  const [searchOpen, setSearchOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState("");
  const searchInputRef = useRef(null);
  const debounceRef = useRef(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const cancelDebounce = t1;
  let t2;
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => cancelDebounce;
    t3 = [cancelDebounce];
    $[1] = t2;
    $[2] = t3;
  } else {
    t2 = $[1];
    t3 = $[2];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[3] !== searchQuery) {
    t4 = () => {
      setLocalSearch(searchQuery);
    };
    t5 = [searchQuery];
    $[3] = searchQuery;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== onSearchChange) {
    t6 = value => {
      setLocalSearch(value);
      cancelDebounce();
      debounceRef.current = setTimeout(() => {
        onSearchChange(value);
      }, 300);
    };
    $[6] = onSearchChange;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  const handleSearchInput = t6;
  let t7;
  if ($[8] !== onSearchChange) {
    t7 = () => {
      setSearchOpen(prev => {
        const next = !prev;
        if (next) {
          requestAnimationFrame(() => searchInputRef.current?.focus());
        } else {
          cancelDebounce();
          setLocalSearch("");
          onSearchChange("");
        }
        return next;
      });
    };
    $[8] = onSearchChange;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const toggleSearch = t7;
  let t8;
  if ($[10] !== categoryOptions || $[11] !== dateFrom || $[12] !== dateTo || $[13] !== filterGroups || $[14] !== hasDateFilter || $[15] !== onCategoryRemove || $[16] !== onDateRangeChange || $[17] !== onFilterRemove || $[18] !== selectedCategories || $[19] !== selectedFilters) {
    t8 = {
      categoryOptions,
      selectedCategories,
      filterGroups,
      selectedFilters,
      dateFrom,
      dateTo,
      hasDateFilter,
      onCategoryRemove,
      onFilterRemove,
      onDateRangeChange
    };
    $[10] = categoryOptions;
    $[11] = dateFrom;
    $[12] = dateTo;
    $[13] = filterGroups;
    $[14] = hasDateFilter;
    $[15] = onCategoryRemove;
    $[16] = onDateRangeChange;
    $[17] = onFilterRemove;
    $[18] = selectedCategories;
    $[19] = selectedFilters;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const {
    chips,
    handleChipRemove
  } = useFilterChips(t8);
  let t10;
  let t11;
  let t9;
  if ($[21] !== chips || $[22] !== handleChipRemove || $[23] !== onClearFilters || $[24] !== onFilterClick || $[25] !== t) {
    const visibleChips = chips.slice(0, MAX_VISIBLE_CHIPS);
    const overflowCount = chips.length - MAX_VISIBLE_CHIPS;
    const hasActiveFilters = chips.length > 0;
    t9 = "col-span-full flex flex-wrap items-center gap-3 md:py-4";
    let t12;
    if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = <Icon name="filter-list" className="size-5" />;
      $[29] = t12;
    } else {
      t12 = $[29];
    }
    let t13;
    if ($[30] !== t) {
      t13 = t("filterCategories");
      $[30] = t;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    let t14;
    if ($[32] !== t13) {
      t14 = <span>{t13}</span>;
      $[32] = t13;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    if ($[34] !== onFilterClick || $[35] !== t14) {
      t10 = <button type="button" onClick={onFilterClick} className="flex cursor-pointer items-center gap-2 typography-component-sm-medium desktop:typography-component-md-medium text-text-primary hover:text-text-secondary transition-colors">{t12}{t14}</button>;
      $[34] = onFilterClick;
      $[35] = t14;
      $[36] = t10;
    } else {
      t10 = $[36];
    }
    t11 = hasActiveFilters && <>{visibleChips.map(chip => <button key={`${chip.groupKey}-${chip.id}`} type="button" onClick={() => handleChipRemove(chip)} className="cursor-pointer"><Tag variant="light" size="sm">{chip.name}<Icon name="close" className="ml-1 size-3" /></Tag></button>)}{overflowCount > 0 && <button type="button" onClick={onFilterClick} className="cursor-pointer"><Tag variant="light" size="sm">+{overflowCount} more</Tag></button>}<button type="button" onClick={onClearFilters} className="flex cursor-pointer items-center gap-1 typography-body-sm-medium text-text-primary hover:text-text-secondary transition-colors"><span>{t("clearAll")}</span><Icon name="close" className="size-3" /></button></>;
    $[21] = chips;
    $[22] = handleChipRemove;
    $[23] = onClearFilters;
    $[24] = onFilterClick;
    $[25] = t;
    $[26] = t10;
    $[27] = t11;
    $[28] = t9;
  } else {
    t10 = $[26];
    t11 = $[27];
    t9 = $[28];
  }
  let t12;
  let t13;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cn("ml-auto flex cursor-pointer items-center gap-2 typography-component-sm-medium desktop:typography-component-md-medium text-text-primary", "hover:text-text-secondary transition-colors");
    t13 = <Icon name="search" className="size-5" />;
    $[37] = t12;
    $[38] = t13;
  } else {
    t12 = $[37];
    t13 = $[38];
  }
  let t14;
  if ($[39] !== t) {
    t14 = t("search");
    $[39] = t;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  let t15;
  if ($[41] !== t14) {
    t15 = <span>{t14}</span>;
    $[41] = t14;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  let t16;
  if ($[43] !== searchOpen || $[44] !== t15 || $[45] !== toggleSearch) {
    t16 = <button type="button" onClick={toggleSearch} aria-expanded={searchOpen} aria-controls="directory-search-panel" className={t12}>{t13}{t15}</button>;
    $[43] = searchOpen;
    $[44] = t15;
    $[45] = toggleSearch;
    $[46] = t16;
  } else {
    t16 = $[46];
  }
  let t17;
  if ($[47] !== t10 || $[48] !== t11 || $[49] !== t16 || $[50] !== t9) {
    t17 = <div className={t9}>{t10}{t11}{t16}</div>;
    $[47] = t10;
    $[48] = t11;
    $[49] = t16;
    $[50] = t9;
    $[51] = t17;
  } else {
    t17 = $[51];
  }
  let t18;
  if ($[52] !== handleSearchInput || $[53] !== localSearch || $[54] !== searchLabel || $[55] !== searchOpen || $[56] !== t) {
    t18 = searchOpen && <div id="directory-search-panel" className="col-span-full"><label htmlFor="directory-search" className="sr-only">{t("search")} {searchLabel}</label><input id="directory-search" ref={searchInputRef} type="text" value={localSearch} onChange={e => handleSearchInput(e.target.value)} placeholder={t("searchPlaceholder", {
        label: searchLabel
      })} className={cn("w-full rounded-sm border border-border-default px-4 py-3", "typography-body-sm-regular text-text-primary placeholder:text-text-secondary", "focus:border-border-active focus:outline-none")} /></div>;
    $[52] = handleSearchInput;
    $[53] = localSearch;
    $[54] = searchLabel;
    $[55] = searchOpen;
    $[56] = t;
    $[57] = t18;
  } else {
    t18 = $[57];
  }
  let t19;
  if ($[58] !== t17 || $[59] !== t18) {
    t19 = <>{t17}{t18}</>;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  return t19;
}